/**
 * Query History Search & Filter
 *
 * Pure utilities for searching and filtering query history entries
 * across all connection/database contexts.
 */

import type { QueryHistoryEntry, QueryHistoryContextKey } from '../types/queryHistoryTypes';

export type DateRangeFilter = 'today' | 'last-7-days' | 'last-30-days' | 'all';
export type SuccessFilter = 'all' | 'success' | 'error';

export type QueryHistorySearchFilters = {
  /** Full-text search across SQL text (case-insensitive substring) */
  searchText: string;
  /** Filter by connection ID */
  connectionId?: string;
  /** Filter by database name */
  database?: string;
  /** Filter by success/error status */
  successFilter: SuccessFilter;
  /** Filter by date range */
  dateRange: DateRangeFilter;
};

export const defaultSearchFilters: QueryHistorySearchFilters = {
  searchText: '',
  successFilter: 'all',
  dateRange: 'all',
};

/**
 * Flatten all history entries from all contexts into a single array.
 */
export function flattenHistory(
  historyByContext: Record<QueryHistoryContextKey, QueryHistoryEntry[]>,
): QueryHistoryEntry[] {
  return Object.values(historyByContext).flat();
}

/**
 * Get the start-of-day Date for a given date range filter.
 * Returns undefined for 'all'.
 */
export function getDateRangeCutoff(range: DateRangeFilter, now = new Date()): Date | undefined {
  if (range === 'all') return undefined;

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (range === 'today') return startOfToday;
  if (range === 'last-7-days') {
    return new Date(startOfToday.getTime() - 6 * 24 * 60 * 60 * 1000);
  }
  // last-30-days
  return new Date(startOfToday.getTime() - 29 * 24 * 60 * 60 * 1000);
}

/**
 * Check if a single entry matches the given filters.
 */
export function matchesFilters(
  entry: QueryHistoryEntry,
  filters: QueryHistorySearchFilters,
  now = new Date(),
): boolean {
  // Text search (case-insensitive substring)
  if (filters.searchText) {
    const needle = filters.searchText.toLowerCase();
    if (!entry.sql.toLowerCase().includes(needle)) return false;
  }

  // Connection filter
  if (filters.connectionId && entry.connectionId !== filters.connectionId) return false;

  // Database filter
  if (filters.database && entry.database !== filters.database) return false;

  // Success/error filter
  if (filters.successFilter === 'success' && !entry.success) return false;
  if (filters.successFilter === 'error' && entry.success) return false;

  // Date range filter
  const cutoff = getDateRangeCutoff(filters.dateRange, now);
  if (cutoff) {
    const entryDate = entry.executedAt instanceof Date ? entry.executedAt : new Date(entry.executedAt);
    if (entryDate < cutoff) return false;
  }

  return true;
}

/**
 * Search and filter all history entries, sorted by executedAt descending.
 */
export function searchQueryHistory(
  historyByContext: Record<QueryHistoryContextKey, QueryHistoryEntry[]>,
  filters: QueryHistorySearchFilters,
  now = new Date(),
): QueryHistoryEntry[] {
  const all = flattenHistory(historyByContext);
  const matched = all.filter((entry) => matchesFilters(entry, filters, now));

  // Sort by executedAt descending (most recent first)
  matched.sort((a, b) => {
    const dateA = a.executedAt instanceof Date ? a.executedAt.getTime() : new Date(a.executedAt).getTime();
    const dateB = b.executedAt instanceof Date ? b.executedAt.getTime() : new Date(b.executedAt).getTime();
    return dateB - dateA;
  });

  return matched;
}

/**
 * Extract unique connection IDs from all history entries.
 */
export function getUniqueConnectionIds(
  historyByContext: Record<QueryHistoryContextKey, QueryHistoryEntry[]>,
): string[] {
  const ids = new Set<string>();
  for (const entries of Object.values(historyByContext)) {
    for (const entry of entries) {
      ids.add(entry.connectionId);
    }
  }
  return Array.from(ids);
}

/**
 * Extract unique database names from all history entries (optionally filtered by connectionId).
 */
export function getUniqueDatabases(
  historyByContext: Record<QueryHistoryContextKey, QueryHistoryEntry[]>,
  connectionId?: string,
): string[] {
  const dbs = new Set<string>();
  for (const entries of Object.values(historyByContext)) {
    for (const entry of entries) {
      if (connectionId && entry.connectionId !== connectionId) continue;
      if (entry.database) dbs.add(entry.database);
    }
  }
  return Array.from(dbs).sort();
}
